import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';

import { ALL_OPTION_KEY, RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';

import { useGetEmployerIdParam } from '../../hooks';
import { useBaseReportTableQuery } from '../../queries';
import { ParticipantAccountActivityReportTableParams } from '../ParticipantActivityReport.types';

export const QUERY_KEY = 'PARTICIPANT_ACCOUNT_ACTIVITY_REPORT_QUERY';

const MIN_SEARCH_LEN = 3;
const useParticipantAccountActivityReportQuery = (
  params: Partial<ParticipantAccountActivityReportTableParams>
) => {
  const getEmployerIdParam = useGetEmployerIdParam();
  const formattedAsDate = useMemo(() => {
    if (!params.asDate) {
      return null;
    }
    const parsedDate = dayjs(params.asDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.asDate]);

  const currentSearch = useMemo(() => {
    if (!!params?.searchString && params?.searchString?.length >= MIN_SEARCH_LEN) {
      return params.searchString;
    }
    return null;
  }, [params.searchString]);

  const employerIdsString = useMemo(() => params?.employerIds?.join(','), [params?.employerIds]);

  const base = useBaseReportTableQuery(
    API_URL.REPORTS_PARTICIPANT_ACCOUNT_ACTIVITY_REPORT_BREAKDOWN,
    [
      QUERY_KEY,
      params?.reportPlanIds?.join(','),
      currentSearch,
      params.employerId,
      params?.planYearPeriod?.join(','),
      formattedAsDate,
      params.enrollmentStatuses?.join(','),
      employerIdsString,
      params.accountTypes?.join(','),
    ],
    {
      ...getEmployerIdParam(params.employerId, params.employerIds),
      show_ssn: true,
      isSSNShowed: params.isSSNShowed,
      as_of_date: formattedAsDate,
      plan_year_period:
        params?.planYearPeriod && !params?.planYearPeriod.includes(ALL_OPTION_KEY)
          ? params?.planYearPeriod.join(',')
          : undefined,
      search_value: currentSearch,
      enrollment_statuses:
        params.enrollmentStatuses && !params.enrollmentStatuses.includes(ALL_OPTION_KEY)
          ? params.enrollmentStatuses.join(',')
          : undefined,
      account_types:
        (params.isMultipleEmployer || params.accountTypes) && !params.accountTypes?.includes(ALL_OPTION_KEY)
          ? params.accountTypes?.join(',')
          : undefined,
      plan_ids:
        params?.reportPlanIds && !params?.reportPlanIds.includes(ALL_OPTION_KEY)
          ? params?.reportPlanIds.join(',')
          : undefined,
    },
    {
      isMultipleEmployers: params.isMultipleEmployer,
      isPreviewMode: params.isPreviewMode,
    }
  );
  useEffect(() => {
    base.onChangePlanOptions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.employerId, employerIdsString]);

  useEffect(() => {
    base.setPage(0);
    base.setRawContentList([]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params?.asDate,
    params?.enrollmentStatuses,
    params?.reportPlanIds,
    params?.planYearPeriod,
    params?.searchString,
    params.employerId,
    params?.accountTypes,
    employerIdsString,
  ]);
  return base;
};

export default useParticipantAccountActivityReportQuery;
